import { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Copy, Check, Smartphone, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export const Give = () => {
  const [copied, setCopied] = useState(false);
  const cashappTag = '$ChristRefugeCC1';
  const paypalPhone = '816-876-0470';

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${type} copied to clipboard!`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="give-page" className="min-h-screen">
      <section 
        className="py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #CD853F 0%, #DAA520 50%, #B8860B 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute bottom-20 right-20 text-white/5 transform rotate-12">
          <svg width="180" height="180" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-white/90">Give</span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mt-4 drop-shadow-lg">
              Partner With Us
            </h1>
            <p className="text-xl text-white/95 mt-6 max-w-3xl mx-auto">
              Your generosity helps us reach the lost, empower the broken, and activate leaders for God's kingdom.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-8">
            {/* CashApp Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="cashapp-card"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-2xl border-l-4 border-primary"
            >
              <div className="flex flex-col items-center text-center space-y-8">
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-amber-600 rounded-full flex items-center justify-center shadow-lg">
                  <DollarSign className="w-10 h-10 text-white" />
                </div>

                <div>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
                    Give via CashApp
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Support our ministry with a secure and easy donation
                  </p>
                </div>

                <div className="w-full space-y-6">
                  <div className="bg-background/50 rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-primary" />
                        <div className="text-left">
                          <p className="text-sm text-muted-foreground">CashApp Tag</p>
                          <p className="text-2xl font-heading font-bold text-primary">{cashappTag}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(cashappTag, 'CashApp tag')}
                        data-testid="copy-cashapp-tag-button"
                        className="p-3 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors duration-300"
                        aria-label="Copy CashApp tag"
                      >
                        {copied ? <Check className="w-5 h-5 text-primary" /> : <Copy className="w-5 h-5 text-primary" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href={`https://cash.app/${cashappTag.substring(1)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="open-cashapp-button"
                      className="flex-1 bg-gradient-to-r from-primary to-amber-600 text-white py-4 rounded-full font-semibold text-lg hover:shadow-2xl transition-all duration-300 text-center"
                    >
                      Open CashApp
                    </a>
                  </div>

                  <div className="bg-primary/5 rounded-2xl p-6 w-full">
                    <h3 className="font-heading font-bold text-foreground mb-3 text-lg">How to Give</h3>
                    <ol className="space-y-2 text-left text-muted-foreground">
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                        <span>Open your CashApp application</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                        <span>Search for <strong className="text-primary">{cashappTag}</strong></span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                        <span>Enter your donation amount and complete the transaction</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* PayPal Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="paypal-card"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-2xl border-l-4 border-secondary"
            >
              <div className="flex flex-col items-center text-center space-y-8">
                <div className="w-20 h-20 bg-gradient-to-br from-secondary to-green-700 rounded-full flex items-center justify-center shadow-lg">
                  <DollarSign className="w-10 h-10 text-white" />
                </div>

                <div>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
                    Give via PayPal
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Send your gift securely through PayPal
                  </p>
                </div>

                <div className="w-full space-y-6">
                  <div className="bg-background/50 rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-secondary" />
                        <div className="text-left">
                          <p className="text-sm text-muted-foreground">PayPal Phone</p>
                          <p className="text-2xl font-heading font-bold text-secondary">{paypalPhone}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(paypalPhone, 'PayPal phone')}
                        data-testid="copy-paypal-phone-button"
                        className="p-3 rounded-lg bg-secondary/10 hover:bg-secondary/20 transition-colors duration-300"
                        aria-label="Copy PayPal phone"
                      >
                        {copied ? <Check className="w-5 h-5 text-secondary" /> : <Copy className="w-5 h-5 text-secondary" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://www.paypal.com/paypalme"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="open-paypal-button"
                      className="flex-1 bg-gradient-to-r from-secondary to-green-700 text-white py-4 rounded-full font-semibold text-lg hover:shadow-2xl transition-all duration-300 text-center"
                    >
                      Open PayPal
                    </a>
                  </div>

                  <div className="bg-secondary/5 rounded-2xl p-6 w-full">
                    <h3 className="font-heading font-bold text-foreground mb-3 text-lg">How to Give</h3>
                    <ol className="space-y-2 text-left text-muted-foreground">
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                        <span>Open PayPal app or visit paypal.com</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                        <span>Send payment to <strong className="text-secondary">{paypalPhone}</strong></span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                        <span>Enter your donation amount and complete the transaction</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section 
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B7355 0%, #A0826D 50%, #8B7355 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '600px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        <div className="absolute top-10 left-10 text-white/5 transform -rotate-12">
          <svg width="150" height="150" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="10" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div data-testid="impact-card-1" className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <Heart className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-heading font-bold text-foreground mb-2">Community Impact</h3>
              <p className="text-sm text-muted-foreground">
                Your giving helps us serve those in need in our community
              </p>
            </div>

            <div data-testid="impact-card-2" className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <Heart className="w-8 h-8 text-secondary mx-auto mb-3" />
              <h3 className="font-heading font-bold text-foreground mb-2">Ministry Growth</h3>
              <p className="text-sm text-muted-foreground">
                Support the expansion of our ministries and outreach programs
              </p>
            </div>

            <div data-testid="impact-card-3" className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <Heart className="w-8 h-8 text-accent mx-auto mb-3" />
              <h3 className="font-heading font-bold text-foreground mb-2">Leader Development</h3>
              <p className="text-sm text-muted-foreground">
                Invest in raising up the next generation of God's generals
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section 
        data-testid="scripture-section" 
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #DEB887 0%, #D2B48C 50%, #BC8F8F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-2xl">
              <p className="text-2xl md:text-3xl font-heading italic text-foreground mb-4 leading-relaxed">
                "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver."
              </p>
              <p className="text-primary font-semibold text-lg">2 Corinthians 9:7</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
